import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { HomeNav } from "@/components/home-nav";
import { HomeFilmSequence } from "@/components/home-film-sequence";
import { IntroOverlay } from "@/components/intro-overlay";
import { ProjectsRail } from "@/components/projects-rail";
import { SiteFooter } from "@/components/site-footer";

import ogGif from "@/assets/og-projects.gif.asset.json";

import { useI18n } from "@/lib/i18n";
import { localizedCoreHead } from "@/lib/core-head";

export const Route = createFileRoute("/")({
  head: ({ matches }) => localizedCoreHead("/", matches[0]?.context.getLang() ?? "en", {
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/" },
      { title: "Luiza Jesus do Prado — Transdisciplinary Artist" },
      {
        name: "description",
        content:
          "Brazilian political artist and researcher in art, science and technology: mental health, ritual, decolonization and Indigenous knowledge.",
      },
      {
        name: "keywords",
        content:
          "Brazilian artist, Brazilian artists, political art, political artist, art science and technology, mental health art, decolonization, decolonial art, ritual, psilocybin, psilocybin art, Indigenous, butoh, performance art, Luiza Jesus do Prado",
      },
      { property: "og:title", content: "Luiza Jesus do Prado — Transdisciplinary Artist" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:description",
        content:
          "Political art across science and technology, mental health, ritual, decolonization and Indigenous knowledge.",
      },
      { property: "og:image", content: `https://www.luizajesusdoprado.com${ogGif.url}` },
      { name: "twitter:image", content: `https://www.luizajesusdoprado.com${ogGif.url}` },
    ],
  }),
  component: Index,
});

function Index() {
  const { t, tx } = useI18n();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);


  

  return (
    <div className="min-h-screen bg-ink text-bone">
      <IntroOverlay />
      <HomeNav />

      <main id="main-content">
        {/* ————— Cinematic stage ————— */}
        <section className="relative h-[100svh] w-full overflow-hidden">
          <div
            className="absolute inset-0 will-change-transform"
            style={{ transform: `translate3d(0, ${scrollY * 0.35}px, 0) scale(${1 + scrollY * 0.00012})` }}
          >
          <HomeFilmSequence playing />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/45 to-ink" />
          <div aria-hidden="true" className="film-grain pointer-events-none absolute -inset-[10%] z-10" />
          <div className="edge-blur-left pointer-events-none absolute inset-y-0 left-0 z-20 w-[18vw]" />
          <div className="edge-blur-right pointer-events-none absolute inset-y-0 right-0 z-20 w-[18vw]" />

          {/* left rail */}
          <div className="absolute bottom-28 left-6 z-30 hidden md:block md:left-12">
            <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-bone/65">
              {t("scrollIndex")}
            </span>
            <Link
              to="/works"
              className="mt-2 inline-flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.3em] text-bone/80 transition-colors hover:text-bone"
            >
              {t("works")} <span className="h-px w-8 bg-bone/60" />
            </Link>
          </div>

          {/* centre — film-title plate */}
          <div
            className="relative z-30 flex h-full flex-col items-center justify-center px-6 text-center will-change-transform"
            style={{ transform: `translate3d(0, ${scrollY * -0.12}px, 0)`, opacity: Math.max(0, 1 - scrollY / 620) }}
          >
            <span className="font-mono text-[9px] uppercase tracking-[0.42em] text-bone/65">
              {tx({ en: "A body of work by", pt: "Um corpo de trabalho de", es: "Un cuerpo de obra de" })}
            </span>

            <h1 className="mt-6 font-didone leading-[0.86] text-bone">
              <span className="block text-[clamp(3.1rem,10.5vw,9rem)] font-medium tracking-[0.02em]">
                Luiza
              </span>
              <span className="-mt-[0.04em] block text-[clamp(2.7rem,8.6vw,7.4rem)] font-light italic tracking-[0.02em] text-bone/90">
                Jesus do Prado
              </span>
            </h1>

            <span className="credit-rule mt-8 block h-px w-[min(38ch,70vw)]" />

            <p className="mt-6 max-w-xl font-mono text-[9px] uppercase leading-[2.1] tracking-[0.26em] text-bone/70">
              {tx({ en: "Transdisciplinary artist · researcher · scientist", pt: "Artista transdisciplinar · pesquisadora · cientista", es: "Artista transdisciplinar · investigadora · científica" })}
            </p>
          </div>
        </section>

        {/* ————— Selected projects rail ————— */}
        <ProjectsRail />
      </main>

      <SiteFooter />
    </div>
  );
}
